import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, startWith } from 'rxjs';
import { AuthService } from 'src/app/_services/auth.service';
import { ProfileService } from 'src/app/_services/profile.service';
import { UtilityService } from 'src/app/_services/utility.service';

@Component({
  selector: 'app-new-header',
  templateUrl: './new-header.component.html',
  styleUrls: ['./new-header.component.css']
})
export class NewHeaderComponent implements OnInit {
  private readonly _profileService: ProfileService = inject(ProfileService);
  public readonly _utilityService: UtilityService = inject(UtilityService);
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _router: Router = inject(Router);
  private readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Input() isSidebarHovered: boolean = false;

  loggedUser: any;
  roleUserLogged: string = '';
  lastSegment: string = '';
  pageTitle: string = '';
  isAdmin: boolean = false;
  url: string = '';
  showGoBackButton: boolean = false;

  constructor() {}

  ngOnInit(): void {
    // Recupero profilo e ruolo utente
    this._profileService.getUserRoleAsync().then(user => {
      this.loggedUser = user;
      this.roleUserLogged = this.loggedUser.roles[0].name;
    });

    this._profileService.isAdmin().then(isAdmin => {
      this.isAdmin = isAdmin;
    });

    // Gestione titolo pagina
    this._utilityService.getTitle().subscribe((title: string) => {
      this.pageTitle = title;
      this._cdr.detectChanges();
    });

    // Gestione URL (sia al primo caricamento che alle navigazioni)
    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        startWith(new NavigationEnd(0, this._router.url, this._router.url))
      )
      .subscribe(event => {
        this.url = event.url;
        this.lastSegment = this.getLastSegment(event.url);
        this.updateGoBackButton(event.url);
      });
  }

  updateGoBackButton(url: string): void {
    const segments = url.split('/').filter(Boolean);
    const meaningfulSegments = segments.filter(
      s => s !== 'secure' && s !== 'dashboard'
    );
    this.showGoBackButton = meaningfulSegments.length > 1;
  }

  logout(): void {
    this._authService.logout().subscribe(res => {
      this._profileService.userLogged.next(null);
      this._profileService.profileLoaded.next(false);
      this._router.navigate(['/login']);
    });
  }

  getLastSegment(url: string): string {
    const segments = url.split('/');
    const lastSegment = segments[segments.length - 1];

    // Verifica se l'ultimo segmento è un numero
    if (!isNaN(Number(lastSegment))) {
      // Se è un numero, restituisci il segmento precedente
      const penultimateSegment = segments[segments.length - 2];
      return penultimateSegment.replace(/-/g, ' ').toUpperCase(); // Sostituisci tutti i trattini con spazi
    } else {
      // Altrimenti, restituisci l'ultimo segmento
      return lastSegment.replace(/-/g, ' ').toUpperCase(); // Sostituisci tutti i trattini con spazi
    }
  }

  /**
   * Funzione che serve per ricavare la descrizione della sezione in base a dove ci troviamo
   * @returns descrizione
   */
  getDescription(): string {
    let description = '';

    switch (this.pageTitle) {
      case 'Competenze':
        description =
          "In questa sezione trovi una panoramica completa delle competenze, sia tecniche che trasversali. Ogni competenza è accompagnata da un doppio sistema di valutazione: un'autovalutazione fornita dall'utente stesso e una valutazione esterna attribuita dagli altri utenti. Questo approccio offre una visione equilibrata e trasparente delle reali capacità, valorizzando sia il punto di vista personale che il riconoscimento da parte della collettività.";
        break;
      case 'Consapevolezza':
        description =
          'Raccogli le consapevolezze che emergono dal lavoro di ogni giorno. Scrivi cosa hai capito, realizzato o scoperto, che sia durante la programmazione, il supporto o la formazione. Ogni team ha una card con le sue attività, organizzate per priorità. Quando completi una ToDo, puoi aggiungere una riflessione legata a quell’esperienza.';
        break;
      case 'Condivisione':
        description =
          'Uno spazio dedicato allo scambio di supporto tra utenti. Qui puoi registrare quando hai dato o ricevuto aiuto, specificando l’argomento trattato. Un modo semplice per valorizzare la collaborazione e tracciare le esperienze di supporto nel tempo.';
        break;
      case 'Percorso di crescita':
        description =
          'Monitora i tuoi obiettivi, sia mensili che di lungo termine. Ogni traguardo, semplice o complesso, ti guida nel tuo sviluppo personale e professionale, rendendo visibile l’evoluzione del tuo percorso nel tempo.';
        break;
      case 'Progetti':
        description =
          'In questa sezione puoi visualizzare tutti i team di cui fai parte, con una panoramica delle principali statistiche di ciascun gruppo. È uno spazio pensato per tenere traccia del tuo coinvolgimento nei vari team, monitorare l’andamento collettivo e restare sempre aggiornato sul contributo del gruppo.';
        break;
      case 'Suggerimenti':
        description =
          'Una sezione dedicata alla raccolta di segnalazioni e suggerimenti da parte degli utenti dell’applicativo. Qui è possibile inviare feedback relativi a bug riscontrati, proposte di miglioramento, richieste di integrazione di nuove tecnologie e molto altro. Uno spazio pensato per favorire il miglioramento continuo della piattaforma attraverso il contributo diretto di chi la utilizza ogni giorno.';
        break;
      case 'Review':
        description =
          'La sezione Review è pensata per offrire una panoramica strutturata delle performance e del contributo lavorativo di un utente. Attraverso dati operativi, attività svolte e statistiche rilevanti, fornisce un contesto completo per analizzare l’andamento generale, facilitando momenti di valutazione, confronto o feedback tra colleghi.';
        break;
      case 'SBI':
        description =
          'La sezione SBI (Situation – Behavior – Impact) è pensata per raccogliere e valorizzare feedback strutturati e significativi. Ogni scheda descrive una situazione specifica, il comportamento osservato e l’impatto generato, offrendo così uno strumento concreto per riconoscere atteggiamenti positivi, favorire il miglioramento continuo e supportare la crescita professionale. Attraverso questo formato, è possibile documentare episodi rilevanti, stimolare la consapevolezza e promuovere una cultura del feedback costruttivo all’interno del team.';
        break;
      case 'Sondaggi':
        description =
          'In questa sezione puoi creare o rispondere a sondaggi semplici, composti da domande a risposta multipla, valutazione su scala o risposta aperta. Uno strumento utile per raccogliere opinioni, preferenze o impressioni in modo rapido e strutturato.';
        break;
      case 'Feedback 360':
        description =
          'Sezione dedicata alla valutazione delle competenze trasversali. Con il Feedback 360, più utenti valutano un collega su una serie di domande, assegnando punteggi da 0 a 10. I risultati contribuiscono al profilo Competenze dell’utente valutato. L’Autovalutazione segue lo stesso schema, ma viene compilata dall’utente su se stesso. Uno strumento pensato per stimolare il confronto, la consapevolezza e la crescita professionale.';
        break;
    }

    // Queste descrizioni sono state inserite in un blocco apparte perchè bisognava controllare i titoli delle sezioni.
    if (this.pageTitle.includes('Wiki')) {
      description =
        'La sezione Wiki raccoglie librerie, tecnologie e framework utilizzati nei progetti, offrendo un punto di riferimento tecnico per tutti gli sviluppatori. Ogni tecnologia è suddivisa in quattro aree: Documentazione, Consigli utili, Supporto ai colleghi e Prerequisiti. Uno strumento pensato per favorire la condivisione della conoscenza, il supporto reciproco e la crescita continua del team.';
    } else if (this.pageTitle.includes('Documentazione')) {
      description =
        'Uno spazio dove gli utenti autorizzati possono caricare guide, tutorial e materiale utile alla consultazione.';
    } else if (this.pageTitle.includes('Consigli utili')) {
      description =
        'Suggerimenti pratici condivisi da sviluppatori per aiutare altri colleghi nell’utilizzo della tecnologia.';
    } else if (this.pageTitle.includes('Supporto ai colleghi')) {
      description =
        'Area collaborativa in cui è possibile chiedere aiuto su problemi specifici e ricevere risposte e soluzioni dagli altri membri del team, accompagnate da eventuali condivisioni.';
    } else if (this.pageTitle.includes('Prerequisiti')) {
      description =
        'Raccolta di argomenti chiave, classificati per livello di difficoltà (base, intermedio, avanzato), su cui ogni utente può auto-valutarsi. Le valutazioni possono essere confermate da un utente autorizzato, per garantire affidabilità e coerenza.';
    } else if (this.pageTitle.includes('Timesheet')) {
      description =
        'In questa sezione puoi visualizzare il tuo timesheet suddiviso per team, con il dettaglio delle ore dedicate sia alle attività di sviluppo che a quelle di fatturazione. Uno strumento utile per tenere traccia del tempo investito nei progetti, migliorare la pianificazione e garantire una gestione efficace delle risorse.';
    }
    return description;
  }

  /**
   * Funzione che serve per mostrare l'info nelle sezioni
   * @returns boolean
   */
  canShowInfo(): boolean {
    return (
      this.pageTitle == 'Competenze' ||
      this.pageTitle == 'Consapevolezza' ||
      this.pageTitle == 'Condivisione' ||
      this.pageTitle == 'Obiettivi' ||
      this.pageTitle == 'Progetti' ||
      this.pageTitle.includes('Timesheet') ||
      this.pageTitle.includes('Wiki') ||
      this.pageTitle.includes('Documentazione') ||
      this.pageTitle.includes('Consigli utili') ||
      this.pageTitle.includes('Supporto ai colleghi') ||
      this.pageTitle.includes('Prerequisiti') ||
      this.pageTitle == 'Suggerimenti' ||
      this.pageTitle == 'Review' ||
      this.pageTitle == 'SBI' ||
      this.pageTitle == 'Sondaggi' ||
      this.pageTitle == 'Feedback 360'
    );
  }

  /**
   * Funzione che serve per tornare indietro di una sezione
   * @returns void
   */
  goBack(): void {
    const currentUrl = this._router.url;
    const segments = currentUrl.split('/').filter(s => s);
    if (currentUrl.includes('sprints')) {
      // questo è quando ci troviamo sulla tabella scrum
      // questo va a rimuovere nell'array ['progetto', 'sprints', 'id_sprint']
      // es: /secure/dashboard/Progetti/agrilog/sprints/463 -> va a rimuovere agrilog/sprints/463
      // perchè vogliamo tornare alla alla pagina /secure/dashboard/Progetti
      segments.splice(-3);
    } else if (currentUrl.includes('survey-detail')) {
      // questo è quando ci troviamo sulla riepilogo sondaggi
      // questo va a rimuovere nell'array ['survey-detail', 'id_feedback']
      // es: /secure/dashboard/sondaggi/survey-detail/11 -> va a rimuovere /survey-detail/11
      // perchè vogliamo tornare alla alla pagina /secure/dashboard/survey
      segments.splice(-2);
    } else {
      // questo quando ci troviamo sul resto del sito
      if (segments.length <= 1) {
        this._router.navigateByUrl('/');
        return;
      }
      segments.pop();
    }
    const parentUrl = '/' + segments.join('/');
    this._router.navigateByUrl(parentUrl);
  }
}
